import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Lock, Users, CalendarDays, BarChart3, Eye, Check, X, Clock, LogOut } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeaders = () => ({ headers: { Authorization: `Bearer ${sessionStorage.getItem("vs_admin_token") || ""}` } });

function AdminLogin({ onLogin }) {
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API}/admin/login`, { password });
      sessionStorage.setItem("vs_admin_token", res.data.token);
      onLogin(true);
      toast.success("Logged in successfully");
    } catch {
      toast.error("Invalid password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0f1419] flex items-center justify-center p-6" data-testid="admin-login">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="glass-card rounded-2xl p-10 max-w-sm w-full text-center">
        <div className="w-16 h-16 rounded-full bg-gold/10 flex items-center justify-center mx-auto mb-6">
          <Lock className="w-7 h-7 text-gold" />
        </div>
        <h1 className="font-heading text-2xl font-bold text-white mb-2">Admin Panel</h1>
        <p className="text-white/40 text-sm mb-8">Enter admin password to continue</p>
        <form onSubmit={handleLogin}>
          <input
            type="password"
            data-testid="admin-password-input"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full bg-black/50 border border-white/10 focus:border-gold/50 text-white rounded-lg p-3 text-sm placeholder:text-white/20 focus:ring-1 focus:ring-gold/50 transition-all outline-none mb-4"
            placeholder="Enter password"
          />
          <button
            type="submit"
            disabled={loading}
            data-testid="admin-login-btn"
            className="w-full bg-gold text-black font-bold py-3 rounded-full hover:shadow-[0_0_20px_rgba(255,191,0,0.4)] transition-all disabled:opacity-50"
          >
            {loading ? "Logging in..." : "Login"}
          </button>
        </form>
      </motion.div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, color }) {
  return (
    <div className="glass-card rounded-xl p-6">
      <div className="flex items-center justify-between mb-4">
        <div className={`w-10 h-10 rounded-lg ${color} flex items-center justify-center`}>
          <Icon className="w-5 h-5 text-gold" />
        </div>
      </div>
      <p className="font-heading text-3xl font-bold text-white">{value}</p>
      <p className="text-white/50 text-sm mt-1">{label}</p>
    </div>
  );
}

function StatusBadge({ status }) {
  const colors = {
    new: "bg-blue-500/20 text-blue-400",
    contacted: "bg-yellow-500/20 text-yellow-400",
    converted: "bg-green-500/20 text-green-400",
    pending: "bg-yellow-500/20 text-yellow-400",
    confirmed: "bg-green-500/20 text-green-400",
    cancelled: "bg-red-500/20 text-red-400",
    completed: "bg-green-500/20 text-green-400",
  };
  return (
    <span className={`px-2 py-1 rounded-full text-xs font-medium ${colors[status] || "bg-white/10 text-white/50"}`}>
      {status}
    </span>
  );
}

export default function AdminPage() {
  const [authenticated, setAuthenticated] = useState(false);
  const [stats, setStats] = useState({});
  const [leads, setLeads] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!authenticated) return;
    const fetchData = async () => {
      setLoading(true);
      try {
        const [statsRes, leadsRes, bookingsRes] = await Promise.all([
          axios.get(`${API}/stats`, authHeaders()),
          axios.get(`${API}/leads`, authHeaders()),
          axios.get(`${API}/bookings`, authHeaders()),
        ]);
        setStats(statsRes.data);
        setLeads(leadsRes.data);
        setBookings(bookingsRes.data);
      } catch {
        toast.error("Failed to load data");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [authenticated]);

  const updateLeadStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/leads/${id}/status`, { status }, authHeaders());
      setLeads(leads.map((l) => (l.id === id ? { ...l, status } : l)));
      toast.success("Lead status updated");
    } catch {
      toast.error("Failed to update status");
    }
  };

  const updateBookingStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/bookings/${id}/status`, { status }, authHeaders());
      setBookings(bookings.map((b) => (b.id === id ? { ...b, status } : b)));
      toast.success("Booking status updated");
    } catch {
      toast.error("Failed to update status");
    }
  };

  if (!authenticated) return <AdminLogin onLogin={setAuthenticated} />;

  return (
    <div className="min-h-screen bg-[#0f1419]" data-testid="admin-dashboard">
      {/* Header */}
      <header className="border-b border-white/5 section-alt">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between h-16">
          <div className="flex items-center gap-2">
            <span className="font-heading text-lg font-bold text-white">VAN</span>
            <span className="font-heading text-lg font-bold gold-shimmer">SIGN</span>
            <span className="text-white/30 text-sm ml-2">Admin</span>
          </div>
          <button onClick={() => setAuthenticated(false)} data-testid="admin-logout-btn" className="flex items-center gap-2 text-white/50 hover:text-white text-sm transition-colors">
            <LogOut className="w-4 h-4" /> Logout
          </button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 md:px-12 py-8">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8" data-testid="admin-stats">
          <StatCard icon={Users} label="Total Leads" value={stats.total_leads || 0} color="bg-gold/10" />
          <StatCard icon={Eye} label="New Leads" value={stats.new_leads || 0} color="bg-blue-500/10" />
          <StatCard icon={CalendarDays} label="Total Bookings" value={stats.total_bookings || 0} color="bg-gold/10" />
          <StatCard icon={Clock} label="Pending Bookings" value={stats.pending_bookings || 0} color="bg-yellow-500/10" />
        </div>

        {/* Tabs */}
        <Tabs defaultValue="leads" className="w-full">
          <TabsList className="bg-[#1A1A1A] border border-white/10 mb-6">
            <TabsTrigger value="leads" data-testid="tab-leads" className="data-[state=active]:bg-gold data-[state=active]:text-black">
              <Users className="w-4 h-4 mr-2" /> Leads ({leads.length})
            </TabsTrigger>
            <TabsTrigger value="bookings" data-testid="tab-bookings" className="data-[state=active]:bg-gold data-[state=active]:text-black">
              <CalendarDays className="w-4 h-4 mr-2" /> Bookings ({bookings.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="leads" data-testid="leads-table-section">
            {loading ? (
              <div className="text-center py-12 text-white/50">Loading...</div>
            ) : leads.length === 0 ? (
              <div className="text-center py-12 text-white/50 glass-card rounded-2xl">
                <Users className="w-8 h-8 mx-auto mb-3 opacity-30" />
                <p>No leads yet</p>
              </div>
            ) : (
              <div className="glass-card rounded-2xl overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="border-white/10 hover:bg-transparent">
                      <TableHead className="text-white/60">Name</TableHead>
                      <TableHead className="text-white/60">Email</TableHead>
                      <TableHead className="text-white/60">Phone</TableHead>
                      <TableHead className="text-white/60">Service</TableHead>
                      <TableHead className="text-white/60">Status</TableHead>
                      <TableHead className="text-white/60">Date</TableHead>
                      <TableHead className="text-white/60">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {leads.map((lead) => (
                      <TableRow key={lead.id} className="border-white/5 hover:bg-white/5">
                        <TableCell className="text-white font-medium">{lead.first_name} {lead.last_name}</TableCell>
                        <TableCell className="text-white/70">{lead.email}</TableCell>
                        <TableCell className="text-white/70">{lead.phone || "-"}</TableCell>
                        <TableCell className="text-white/70">{lead.service_interest || "-"}</TableCell>
                        <TableCell><StatusBadge status={lead.status} /></TableCell>
                        <TableCell className="text-white/50 text-xs">{new Date(lead.created_at).toLocaleDateString()}</TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <button onClick={() => updateLeadStatus(lead.id, "contacted")} className="p-1.5 rounded-lg hover:bg-yellow-500/10 text-yellow-400" title="Mark Contacted" data-testid={`lead-contact-${lead.id}`}>
                              <Eye className="w-3.5 h-3.5" />
                            </button>
                            <button onClick={() => updateLeadStatus(lead.id, "converted")} className="p-1.5 rounded-lg hover:bg-green-500/10 text-green-400" title="Mark Converted" data-testid={`lead-convert-${lead.id}`}>
                              <Check className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </TabsContent>

          <TabsContent value="bookings" data-testid="bookings-table-section">
            {loading ? (
              <div className="text-center py-12 text-white/50">Loading...</div>
            ) : bookings.length === 0 ? (
              <div className="text-center py-12 text-white/50 glass-card rounded-2xl">
                <CalendarDays className="w-8 h-8 mx-auto mb-3 opacity-30" />
                <p>No bookings yet</p>
              </div>
            ) : (
              <div className="glass-card rounded-2xl overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="border-white/10 hover:bg-transparent">
                      <TableHead className="text-white/60">Name</TableHead>
                      <TableHead className="text-white/60">Email</TableHead>
                      <TableHead className="text-white/60">Phone</TableHead>
                      <TableHead className="text-white/60">Date</TableHead>
                      <TableHead className="text-white/60">Time</TableHead>
                      <TableHead className="text-white/60">Service</TableHead>
                      <TableHead className="text-white/60">Status</TableHead>
                      <TableHead className="text-white/60">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {bookings.map((booking) => (
                      <TableRow key={booking.id} className="border-white/5 hover:bg-white/5">
                        <TableCell className="text-white font-medium">{booking.name}</TableCell>
                        <TableCell className="text-white/70">{booking.email}</TableCell>
                        <TableCell className="text-white/70">{booking.phone}</TableCell>
                        <TableCell className="text-gold">{booking.date}</TableCell>
                        <TableCell className="text-white/70">{booking.time_slot}</TableCell>
                        <TableCell className="text-white/70">{booking.service_interest || "-"}</TableCell>
                        <TableCell><StatusBadge status={booking.status} /></TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <button onClick={() => updateBookingStatus(booking.id, "confirmed")} className="p-1.5 rounded-lg hover:bg-green-500/10 text-green-400" title="Confirm" data-testid={`booking-confirm-${booking.id}`}>
                              <Check className="w-3.5 h-3.5" />
                            </button>
                            <button onClick={() => updateBookingStatus(booking.id, "cancelled")} className="p-1.5 rounded-lg hover:bg-red-500/10 text-red-400" title="Cancel" data-testid={`booking-cancel-${booking.id}`}>
                              <X className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
